import { useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  num,
  pillarsApi,
  signed,
  type PairRow,
  type PairScan,
  type PairTest,
  type ViewFilters,
} from "../../lib/pillars";
import { ACCENT, AXIS, Card, DIM, DOWN, GRID, TOOLTIP, UP } from "./chartkit";

const MODES: { key: string; label: string }[] = [
  { key: "same_sector", label: "Same sector" },
  { key: "cross_sector", label: "Cross-sector only" },
  { key: "all", label: "All pairs" },
];

const SIGNAL: Record<PairRow["signal"], { label: string; cls: string }> = {
  long_spread: { label: "LONG SPREAD", cls: "text-term-up" },
  short_spread: { label: "SHORT SPREAD", cls: "text-term-down" },
  watch: { label: "watch", cls: "text-term-text" },
  flat: { label: "flat", cls: "text-term-dim" },
  unhedged: { label: "not a hedge", cls: "text-term-dim" },
  diverged: { label: "diverged", cls: "text-term-dim" },
};

/**
 * Cointegrated pairs among the largest names in the current sub-universe.
 *
 * The summary line comes before the table on purpose. A scan is hundreds of tests,
 * and "18 pairs found" means nothing until it sits next to "about 25 would pass by
 * chance" — which is frequently the whole result.
 */
export function PairsView({
  filters,
  onError,
}: {
  filters: ViewFilters;
  onError: (e: unknown) => void;
}) {
  const [mode, setMode] = useState("same_sector");
  const [maxNames, setMaxNames] = useState(80);
  const [scan, setScan] = useState<PairScan | null>(null);
  const [busy, setBusy] = useState(false);
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [test, setTest] = useState<PairTest | null>(null);
  const [testing, setTesting] = useState(false);

  const runScan = () => {
    setBusy(true);
    pillarsApi
      .pairsScan(mode, filters, maxNames)
      .then(setScan)
      .catch(onError)
      .finally(() => setBusy(false));
  };
  const runTest = (x: string, y: string) => {
    if (!x || !y) return;
    setA(x);
    setB(y);
    setTesting(true);
    pillarsApi
      .pairsTest(x, y)
      .then(setTest)
      .catch(onError)
      .finally(() => setTesting(false));
  };

  const button =
    "px-3 py-1 rounded border border-term-edge text-[11px] hover:border-term-accent disabled:opacity-40";
  const input =
    "w-24 bg-term-bg border border-term-edge rounded px-2 py-1 text-[11px] uppercase outline-none focus:border-term-accent";
  const universe = [filters.index, filters.sector, filters.size].filter(Boolean).join(" · ") || "whole universe";

  return (
    <div className="p-3 space-y-3">
      <div className="flex flex-wrap items-center gap-3 text-[11px]">
        <button className={button} disabled={busy} onClick={runScan}>
          {busy ? "Scanning…" : "Scan for pairs"}
        </button>
        {MODES.map((m) => (
          <label key={m.key} className="flex items-center gap-1 text-term-dim cursor-pointer">
            <input type="radio" checked={mode === m.key} onChange={() => setMode(m.key)} />
            {m.label}
          </label>
        ))}
        <label className="text-term-dim">
          largest{" "}
          <select
            value={maxNames}
            onChange={(e) => setMaxNames(Number(e.target.value))}
            className="bg-term-bg border border-term-edge rounded px-1 py-0.5"
          >
            {[40, 80, 120, 150].map((n) => (
              <option key={n}>{n}</option>
            ))}
          </select>{" "}
          names in: <span className="text-term-text">{universe}</span>
        </label>
      </div>

      {!scan && (
        <p className="text-[11px] leading-relaxed text-term-dim max-w-3xl">
          Each pair is fitted on two years of adjusted prices and judged on the six months after,
          which the fit never sees. The sub-universe comes from the filters on the left. "All
          pairs" on 150 names is about 11,000 tests and takes a while.
        </p>
      )}

      {scan && (
        <>
          <div className="border border-term-edge rounded px-3 py-2 text-[11px] leading-relaxed">
            <span className="tnum text-term-text">{scan.n_tests.toLocaleString()}</span>
            <span className="text-term-dim"> pairs tested across {scan.n_names} names as of {scan.on}. </span>
            <span className="tnum text-term-text">{scan.n_significant}</span>
            <span className="text-term-dim"> pass p &lt; {scan.alpha}; chance alone predicts about </span>
            <span className="tnum text-term-text">{Math.round(scan.expected_by_chance)}</span>
            <span className="text-term-dim">. </span>
            <span className={scan.n_fdr ? "text-term-up" : "text-term-down"}>
              {scan.n_fdr} survive false-discovery control
            </span>
            <span className="text-term-dim">
              {scan.n_dropped ? ` · ${scan.n_dropped} names skipped for an incomplete price history` : ""}
              {` · ${(scan.elapsed_ms / 1000).toFixed(1)}s`}
            </span>
          </div>

          <table className="w-full text-[12px]">
            <thead className="text-[10px] uppercase tracking-wide text-term-dim">
              <tr className="border-b border-term-edge">
                <th className="text-left px-2 py-1">Pair</th>
                <th className="text-left px-2">Sector</th>
                <th className="text-right px-2">p-value</th>
                <th className="text-right px-2" title="Survives Benjamini-Hochberg at 5%">FDR</th>
                <th className="text-right px-2">Half-life</th>
                <th className="text-right px-2" title="Correlation of daily returns">Corr</th>
                <th className="text-right px-2">Hedge ratio</th>
                <th className="text-right px-2" title="Today's spread in formation standard deviations, out of sample">z-score</th>
                <th className="text-right px-2" title="Does the spread still revert in the held-out six months? Low is good; little power.">OOS p</th>
                <th className="text-left px-2">Signal</th>
              </tr>
            </thead>
            <tbody>
              {scan.pairs.map((p) => (
                <tr
                  key={`${p.a}/${p.b}`}
                  onClick={() => runTest(p.a, p.b)}
                  className="border-b border-term-edge/50 cursor-pointer hover:bg-term-panel"
                >
                  <td className="px-2 py-1 text-term-accent">
                    {p.a} / {p.b}
                  </td>
                  <td className="px-2 text-term-dim">
                    {p.sector_a === p.sector_b ? p.sector_a : `${p.sector_a} / ${p.sector_b}`}
                  </td>
                  <td className="text-right px-2 tnum">{p.p_value.toFixed(4)}</td>
                  <td className={`text-right px-2 ${p.fdr ? "text-term-up" : "text-term-dim"}`}>{p.fdr ? "yes" : "no"}</td>
                  <td className="text-right px-2 tnum">{p.half_life == null ? "—" : `${Math.round(p.half_life)}d`}</td>
                  <td className="text-right px-2 tnum">{signed(p.corr)}</td>
                  <td className={`text-right px-2 tnum ${p.hedge_ratio <= 0 ? "text-term-down" : ""}`}>{num(p.hedge_ratio, 3)}</td>
                  <td className="text-right px-2 tnum">{signed(p.z)}</td>
                  <td className="text-right px-2 tnum">{num(p.oos_p, 3)}</td>
                  <td className={`px-2 ${SIGNAL[p.signal].cls}`}>{SIGNAL[p.signal].label}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {scan.pairs.length === 0 && <p className="text-[11px] text-term-dim">No pair passed.</p>}
        </>
      )}

      <Card
        title="Test your own pair"
        subtitle="Any two tickers in the price store, whether or not a scan surfaced them. One pair chosen in advance is a single test; one chosen because its chart looked good is not, and no statistic can tell them apart afterwards."
        height="auto"
      >
        <div className="flex items-center gap-2 px-1 py-1">
          <input className={input} placeholder="Ticker A" value={a} onChange={(e) => setA(e.target.value.toUpperCase())} />
          <input className={input} placeholder="Ticker B" value={b} onChange={(e) => setB(e.target.value.toUpperCase())} />
          <button className={button} disabled={testing || !a || !b} onClick={() => runTest(a, b)}>
            {testing ? "Testing…" : "Test"}
          </button>
          {test && (
            <span className="text-[11px] text-term-dim">
              <span className="text-term-accent">{test.a} / {test.b}</span> · p{" "}
              <span className="tnum text-term-text">{test.p_value.toFixed(4)}</span> · hedge{" "}
              <span className="tnum text-term-text">{num(test.hedge_ratio, 3)}</span> · half-life{" "}
              <span className="tnum text-term-text">{test.half_life == null ? "—" : `${Math.round(test.half_life)}d`}</span> · z{" "}
              <span className="tnum text-term-text">{signed(test.z)}</span> · OOS p{" "}
              <span className="tnum text-term-text">{num(test.oos_p, 3)}</span> ·{" "}
              <span className={SIGNAL[test.signal].cls}>{SIGNAL[test.signal].label}</span>
            </span>
          )}
        </div>
        {test && (
          <div style={{ height: 260 }}>
            <ResponsiveContainer>
              <LineChart data={test.series} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="date" tick={AXIS} minTickGap={50} tickFormatter={(d: string) => d.slice(0, 7)} />
                <YAxis tick={AXIS} />
                <Tooltip {...TOOLTIP} formatter={(v: number) => signed(v)} />
                <ReferenceArea x1={test.holdout_from} x2={test.series[test.series.length - 1]?.date} fill={ACCENT} fillOpacity={0.06} />
                <ReferenceLine y={0} stroke={DIM} />
                <ReferenceLine y={2} stroke={DOWN} strokeDasharray="3 3" />
                <ReferenceLine y={-2} stroke={UP} strokeDasharray="3 3" />
                <Line dataKey="z" name="spread z-score" stroke={ACCENT} dot={false} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
            <p className="px-2 text-[10px] text-term-dim">
              Shaded: the six months the fit never saw (from {test.holdout_from}). Dashed: ±2, where
              the spread counts as stretched.
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
